"use client";

import { useMemo } from "react";
import {
  type FeatureFlags,
  getFeatureFlags,
  isAuthEnabled,
  isAuthenticatedCheckoutMode,
  isCartEnabled,
  isCheckoutEnabled,
  isCommerceEnabled,
  isFullShippingMode,
  isGuestCheckoutMode,
  isPickupOnlyMode,
  isPurchasingEnabled,
} from "@/lib/feature-flags";

/**
 * Hook for accessing feature flags in React components
 * Provides memoized access to feature flag state
 */
export function useFeatureFlags(): FeatureFlags & {
  isCommerceEnabled: boolean;
  isAuthEnabled: boolean;
  isCartEnabled: boolean;
  isCheckoutEnabled: boolean;
  isPurchasingEnabled: boolean;
  isGuestCheckoutMode: boolean;
  isAuthenticatedCheckoutMode: boolean;
  isPickupOnlyMode: boolean;
  isFullShippingMode: boolean;
} {
  const flags = useMemo(() => getFeatureFlags(), []);

  return useMemo(
    () => ({
      ...flags,
      isCommerceEnabled: isCommerceEnabled(),
      isAuthEnabled: isAuthEnabled(),
      isCartEnabled: isCartEnabled(),
      isCheckoutEnabled: isCheckoutEnabled(),
      isPurchasingEnabled: isPurchasingEnabled(),
      isGuestCheckoutMode: isGuestCheckoutMode(),
      isAuthenticatedCheckoutMode: isAuthenticatedCheckoutMode(),
      isPickupOnlyMode: isPickupOnlyMode(),
      isFullShippingMode: isFullShippingMode(),
    }),
    [flags]
  );
}

/**
 * Hook specifically for commerce-related features
 * Returns boolean indicating if commerce features should be shown
 */
export function useCommerceEnabled(): boolean {
  return useMemo(() => isCommerceEnabled(), []);
}

/**
 * Hook specifically for authentication features
 * Returns boolean indicating if auth features should be shown
 */
export function useAuthEnabled(): boolean {
  return useMemo(() => isAuthEnabled(), []);
}

/**
 * Hook specifically for cart/basket features
 * Returns boolean indicating if cart features should be shown
 */
export function useCartEnabled(): boolean {
  return useMemo(() => isCartEnabled(), []);
}

/**
 * Hook specifically for checkout features
 * Returns boolean indicating if checkout features should be shown
 */
export function useCheckoutEnabled(): boolean {
  return useMemo(() => isCheckoutEnabled(), []);
}

/**
 * Hook specifically for purchasing features
 * Returns boolean indicating if purchasing features should be shown
 */
export function usePurchasingEnabled(): boolean {
  return useMemo(() => isPurchasingEnabled(), []);
}

/**
 * Hook specifically for guest checkout mode
 * Returns boolean indicating if checkout is in guest mode (Phase 1)
 */
export function useGuestCheckoutMode(): boolean {
  return useMemo(() => isGuestCheckoutMode(), []);
}

/**
 * Hook specifically for authenticated checkout mode
 * Returns boolean indicating if checkout is in authenticated mode (Phase 2)
 */
export function useAuthenticatedCheckoutMode(): boolean {
  return useMemo(() => isAuthenticatedCheckoutMode(), []);
}

/**
 * Hook specifically for pickup-only shipping mode
 * Returns boolean indicating if shipping is in pickup-only mode (Phase 1)
 */
export function usePickupOnlyMode(): boolean {
  return useMemo(() => isPickupOnlyMode(), []);
}

/**
 * Hook specifically for full shipping mode
 * Returns boolean indicating if shipping is in full mode (Phase 2)
 */
export function useFullShippingMode(): boolean {
  return useMemo(() => isFullShippingMode(), []);
}
